import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import './Dashboard.scss';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const RANGE_OPTIONS = [
  { label: 'Today',   value: 1  },
  { label: '7 Days',  value: 7  },
  { label: '30 Days', value: 30 },
];

const STAT_CARDS = [
  { key: 'total',     label: 'Total Appointments', color: 'dash-card--blue'   },
  { key: 'pending',   label: 'Pending',             color: 'dash-card--yellow' },
  { key: 'confirmed', label: 'Confirmed',            color: 'dash-card--green'  },
  { key: 'cancelled', label: 'Cancelled',            color: 'dash-card--red'    },
];

const STATUS_BADGE = {
  pending:   'dash-badge dash-badge--pending',
  confirmed: 'dash-badge dash-badge--confirmed',
  cancelled: 'dash-badge dash-badge--cancelled',
};

const PIE_COLORS = ['#583FBC', '#0097a7', '#ffa000', '#ef4444', '#10b981'];

function fmt12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

function fmtDateTick(dateStr) {
  if (!dateStr) return '';
  const [, mo, d] = dateStr.split('-');
  return `${parseInt(mo)}/${parseInt(d)}`;
}

export default function Dashboard() {
  const { authFetch } = useAuth();
  const [range, setRange]             = useState(7);
  const [statsData, setStatsData]     = useState(null);
  const [todaySlots, setTodaySlots]   = useState([]);
  const [recentAppts, setRecentAppts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const [statsRes, slotsRes, apptRes] = await Promise.all([
        authFetch(`${API}/api/appointments/stats?range=${range}`),
        authFetch(`${API}/api/slots/all?date=${today}`),
        authFetch(`${API}/api/appointments/`),
      ]);

      if (!statsRes.ok) throw new Error('Failed to load stats');
      if (!slotsRes.ok) throw new Error('Failed to load slots');
      if (!apptRes.ok) throw new Error('Failed to load appointments');

      const stats = await statsRes.json();
      const slots = await slotsRes.json();
      const appts = await apptRes.json();

      setStatsData(stats);
      setTodaySlots(slots.slice(0, 6));
      setRecentAppts([...appts].reverse().slice(0, 6));
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'Unable to connect to the server.' : err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, range]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Dashboard</h2>
          <p className="dash-date">{today}</p>
        </div>
        <div className="dash-header-right">
          <div className="dash-range-tabs">
            {RANGE_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                className={`dash-range-tab ${range === value ? 'dash-range-tab--active' : ''}`}
                onClick={() => setRange(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <button className="dash-refresh-btn" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading && !statsData ? (
        <div className="dash-loading">
          <div className="dash-spinner" />
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div className="dash-stats-grid">
            {STAT_CARDS.map(({ key, label, color }) => (
              <div key={key} className={`dash-card ${color}`}>
                <div className="dash-card-value">{statsData ? statsData[key] : '—'}</div>
                <div className="dash-card-label">{label}</div>
              </div>
            ))}
            <div className="dash-card dash-card--slate">
              <div className="dash-card-value">{statsData ? statsData.today_slots : todaySlots.length}</div>
              <div className="dash-card-label">Slots Today</div>
            </div>
          </div>

          {/* Charts row */}
          {statsData && (
            <div className="dash-charts-row">
              {/* Booking trend area chart */}
              <div className="dash-chart-panel">
                <div className="dash-panel-header">
                  <h3 className="dash-panel-title">Booking Trend</h3>
                  <span className="dash-panel-badge">
                    Last {range} day{range !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="dash-chart-wrap">
                  {statsData.daily.every(d => d.count === 0) ? (
                    <p className="dash-empty">No bookings in this period.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={185}>
                      <AreaChart data={statsData.daily} margin={{ top: 10, right: 12, left: -24, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#583FBC" stopOpacity={0.18} />
                            <stop offset="95%" stopColor="#583FBC" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={fmtDateTick}
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                          interval={range > 14 ? 4 : range > 7 ? 2 : 0}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <Tooltip
                          formatter={v => [v, 'Bookings']}
                          labelFormatter={l => `Date: ${l}`}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#583FBC"
                          strokeWidth={2}
                          fill="url(#colorBookings)"
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Service breakdown donut */}
              <div className="dash-chart-panel">
                <div className="dash-panel-header">
                  <h3 className="dash-panel-title">By Service</h3>
                  <span className="dash-panel-badge">
                    Last {range} day{range !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="dash-chart-wrap">
                  {!statsData.by_service || statsData.by_service.length === 0 ? (
                    <p className="dash-empty">No bookings in this period.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={185}>
                      <PieChart>
                        <Pie
                          data={statsData.by_service}
                          dataKey="count"
                          nameKey="service"
                          cx="50%"
                          cy="45%"
                          innerRadius={38}
                          outerRadius={62}
                          paddingAngle={3}
                        >
                          {statsData.by_service.map((_, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v, n) => [v, n]}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        />
                        <Legend
                          iconType="circle"
                          iconSize={7}
                          wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bottom two-column panels */}
          <div className="dash-grid-2">
            {/* Today's Schedule */}
            <div className="dash-panel">
              <div className="dash-panel-header">
                <h3 className="dash-panel-title">Today's Schedule</h3>
                <span className="dash-panel-badge">{todaySlots.length}</span>
              </div>
              {todaySlots.length === 0 ? (
                <p className="dash-empty">No slots scheduled for today.</p>
              ) : (
                <ul className="dash-slot-list">
                  {todaySlots.map((slot, i) => (
                    <li key={slot._id || i} className="dash-slot-item">
                      <div className="dash-slot-time">{fmt12(slot.start_time)}</div>
                      <div className="dash-slot-info">
                        <div className="dash-slot-doctor">{slot.doctor_name}</div>
                        <div className="dash-slot-meta">{slot.service} · {slot.department}</div>
                      </div>
                      <span className={`dash-slot-avail ${slot.is_active ? 'active' : 'inactive'}`}>
                        {slot.is_active ? 'Active' : 'Off'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent Appointments */}
            <div className="dash-panel">
              <div className="dash-panel-header">
                <h3 className="dash-panel-title">Recent Appointments</h3>
                <span className="dash-panel-badge">{recentAppts.length}</span>
              </div>
              {recentAppts.length === 0 ? (
                <p className="dash-empty">No appointments yet.</p>
              ) : (
                <ul className="dash-appt-list">
                  {recentAppts.map((a, i) => (
                    <li key={a._id || i} className="dash-appt-item">
                      <div className="dash-appt-avatar">
                        {(a.patient_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="dash-appt-info">
                        <div className="dash-appt-name">{a.patient_name || '—'}</div>
                        <div className="dash-appt-meta">{a.service} · {a.department}</div>
                      </div>
                      <span className={STATUS_BADGE[a.status] || 'dash-badge'}>
                        {a.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
